import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { useMultiCmt } from '../contexts/MultiCmtContext';
import { useTimeline } from '../contexts/TimelineContext';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import PageHeader from '../components/PageHeader';
import { Tooltip, Modal, Input, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useIncidentOverdueTasks } from '../hooks/useIncidentOverdueTasks';
import { INCIDENT_TYPES } from '../constants/incidentTypes';
import { incidentService } from '../services/incidentService';
import cmtService from '../services/cmtService';

const Incidents = () => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const { activeCmt, availableCmts, isMultiCmtEnabled, hasHierarchy, globalCmt } = useMultiCmt();
  const { preloadTimelineData, preloadMultipleIncidents, clearAllTimelineData } = useTimeline();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [includeChildren, setIncludeChildren] = useState(false);
  const [cmtIdToName, setCmtIdToName] = useState({});
  
  // Delete confirmation modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [incidentToDelete, setIncidentToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Get task badge states for all incidents
  const { taskBadgeStates } = useIncidentOverdueTasks();

  // Add CSS animation for critical task badges
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse-critical {
        0% { 
          transform: scale(1);
          opacity: 1;
          box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
        }
        50% { 
          transform: scale(1.15);
          opacity: 0.7;
          box-shadow: 0 0 0 8px rgba(220, 53, 69, 0);
        }
        100% { 
          transform: scale(1);
          opacity: 1;
          box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
        }
      }
      
      .task-badge-critical {
        animation: pulse-critical 1.2s ease-in-out infinite;
        position: relative;
      }
      
      @media (prefers-reduced-motion: reduce) {
        .task-badge-critical {
          animation: none;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    severity: '',
    dateRange: {
      start: '',
      end: ''
    },
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Debug useEffect to track includeChildren state changes
  useEffect(() => {
    console.log('📊 STATE CHANGE: includeChildren is now:', includeChildren);
  }, [includeChildren]);

  useEffect(() => {
    if (!currentUser?.orgId) {
      setError('No organisation ID found. Please try logging out and back in.');
      setLoading(false);
      return;
    }



    const loadIncidents = async () => {
      try {
        console.log('🔥 USEEFFECT TRIGGERED: loadIncidents called with includeChildren:', includeChildren, 'activeCmt:', activeCmt?.id);
        setLoading(true);
        
        // Use the enhanced incident service with hierarchical support
        console.log('🔍 REACT TOGGLE DEBUG: Calling getIncidents with includeChildren:', includeChildren, 'for CMT:', activeCmt?.id);
        const incidentList = await incidentService.getIncidents(
          currentUser.orgId,
          activeCmt?.id,
          {
            includeChildren,
            skipCache: false
          }
        );

        // Incidents loaded

        setIncidents(incidentList);
        setLoading(false);
      } catch (err) {
        console.error('Error loading incidents:', err);
        setError(`Failed to load incidents: ${err.message}`);
        setLoading(false);
      }
    };

    loadIncidents();

    // Cleanup on unmount
    return () => {
      clearAllTimelineData(); // Clear all timeline data when leaving incidents page
    };
  }, [currentUser?.orgId, activeCmt?.id, includeChildren, clearAllTimelineData]);

  // Build an org-wide CMT id->name map to render CMT names consistently for all users
  useEffect(() => {
    const loadCmtMap = async () => {
      try {
        if (!currentUser?.orgId) return;
        const cmts = await cmtService.getOrganizationCMTs(currentUser.orgId);
        const map = Object.fromEntries(cmts.map(c => [c.id, c.name || c.displayName || c.id]));
        setCmtIdToName(map);
      } catch (e) {
        console.warn('Failed to load CMT name map:', e);
      }
    };
    loadCmtMap();
  }, [currentUser?.orgId]);

  // Handle delete incident
  const handleDeleteIncident = (incident) => {
    setIncidentToDelete(incident);
    setDeleteModalVisible(true);
    setDeleteConfirmText('');
  };

  // Confirm delete incident
  const confirmDeleteIncident = async () => {
    if (deleteConfirmText !== 'DELETE') {
      message.error('Please type "DELETE" to confirm');
      return;
    }

    if (!incidentToDelete) return;

    try {
      setDeleting(true);
      
      const incidentRef = doc(db, 'c3_incidents', incidentToDelete.id);
      await updateDoc(incidentRef, {
        status: 'deleted',
        deletedAt: new Date(),
        deletedBy: currentUser.uid
      });

      message.success(`Incident "${incidentToDelete.title}" has been deleted. It will no longer appear in the incidents list but remains in audit logs.`);
      
      // 🔥 INSTANT REMOVAL: Update local state to remove deleted incident immediately
      setIncidents(prevIncidents => 
        prevIncidents.filter(incident => incident.id !== incidentToDelete.id)
      );
      
      // Close modal and reset state
      setDeleteModalVisible(false);
      setIncidentToDelete(null);
      setDeleteConfirmText('');
      setDeleting(false);
    } catch (error) {
      console.error('Error deleting incident:', error);
      message.error('Failed to delete incident');
      setDeleting(false);
    }
  };

  // Cancel delete
  const cancelDeleteIncident = () => {
    setDeleteModalVisible(false);
    setIncidentToDelete(null);
    setDeleteConfirmText('');
  };

     // Preload timeline data for all incidents when they change
   useEffect(() => {
     if (incidents.length > 0) {
       // Preloading timeline data
       const incidentIds = incidents.map(incident => incident.id);
       preloadMultipleIncidents(incidentIds);
     }
   }, [incidents, preloadMultipleIncidents]);

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-danger';
      case 'stabilising':
        return 'bg-warning';
      case 'resolved':
        return 'bg-success';
      case 'exercise':
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  };

  const getSeverityBadgeClass = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'extreme':
        return 'bg-danger';
      case 'high':
        return 'bg-warning';
      case 'moderate':
        return 'bg-info';
      case 'low':
        return 'bg-success';
      default:
        return 'bg-secondary';
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDateRangeChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [key]: value
      }
    }));
  };

  const filteredIncidents = incidents.filter(incident => {
    if (!incident) return false;

    // Search filter
    if (filters.search && !incident.title.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }

    // Status filter
    if (filters.status && incident.status !== filters.status) {
      return false;
    }

    // Type filter
    if (filters.type) {
      // Handle 'Other' type with custom crisis type
      const incidentType = incident.crisisType === 'Other' && incident.customCrisisType 
        ? incident.customCrisisType 
        : incident.crisisType;
      
      if (incidentType !== filters.type) {
        return false;
      }
    }

    // Severity filter (minimum threshold)
    if (filters.severity) {
      const severityLevels = {
        'low': 1,
        'moderate': 2,
        'high': 3,
        'extreme': 4
      };
      
      const filterSeverityLevel = severityLevels[filters.severity.toLowerCase()] || 0;
      const incidentSeverityLevel = severityLevels[incident.severity?.toLowerCase()] || 0;
      
      // Only show incidents with severity >= minimum threshold
      if (incidentSeverityLevel < filterSeverityLevel) {
      return false;
      }
    }

    // Date range filter
    if (filters.dateRange.start) {
      const startDate = new Date(filters.dateRange.start);
      const incidentDate = incident.createdAt?.toDate ? incident.createdAt.toDate() : new Date(incident.createdAt);
      if (incidentDate < startDate) return false;
    }

    if (filters.dateRange.end) {
      const endDate = new Date(filters.dateRange.end);
      const incidentDate = incident.createdAt?.toDate ? incident.createdAt.toDate() : new Date(incident.createdAt);
      if (incidentDate > endDate) return false;
    }

    return true;
  }).sort((a, b) => {
    const aValue = a[filters.sortBy];
    const bValue = b[filters.sortBy];
    
    if (filters.sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  return (
    <div className="p-4">
      <PageHeader title="Incidents" />

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          {error.includes('requires an index') && (
            <div className="mt-2">
              <a 
                href="https://console.firebase.google.com/v1/r/project/crisiscompass-prod/firestore/indexes?create_composite=ClRwcm9qZWN0cy9jcmlzaXNjb21wYXNzLXByb2QvZGF0YWJhc2VzLyhkZWZhdWx0KS9jb2xsZWN0aW9uR3JvdXBzL2luY2lkZW50cy9pbmRleGVzL18QARoJCgVvcmdJZBABGg0KCWNyZWF0ZWRBdBACGgwKCF9fbmFtZV9fEAI" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-danger"
              >
                Create Required Index
              </a>
            </div>
          )}
        </div>
      )}

      {/* Advanced Filters */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3 d-flex align-items-center">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="toggleChildren"
                  checked={includeChildren}
                  onChange={(e) => {
                    console.log('🔄 TOGGLE CLICKED: Previous state:', includeChildren, '→ New state:', e.target.checked);
                    setIncludeChildren(e.target.checked);
                  }}
                />
                <label className="form-check-label ms-2" htmlFor="toggleChildren">
                  Include Direct Children CMTs
                </label>
              </div>
            </div>
            <div className="col-md-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search incidents..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Stabilising">Stabilising</option>
                <option value="Resolved">Resolved</option>
                <option value="Exercise">Exercise</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="">All Types</option>
                {INCIDENT_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={filters.severity}
                onChange={(e) => handleFilterChange('severity', e.target.value)}
              >
                <option value="">All Severities</option>
                <option value="extreme">Minimum: Extreme</option>
                <option value="high">Minimum: High</option>
                <option value="moderate">Minimum: Moderate</option>
                <option value="low">Minimum: Low</option>
              </select>
            </div>
            <div className="col-md-3">
              <div className="input-group">
                <input
                  type="date"
                  className="form-control"
                  value={filters.dateRange.start}
                  onChange={(e) => handleDateRangeChange('start', e.target.value)}
                />
                <span className="input-group-text">to</span>
                <input
                  type="date"
                  className="form-control"
                  value={filters.dateRange.end}
                  onChange={(e) => handleDateRangeChange('end', e.target.value)}
                />
              </div>
            </div>
          </div>
          
          <div className="row mt-3">
            <div className="col-md-3">
              <select
                className="form-select"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <option value="createdAt">Sort by Date</option>
                <option value="title">Sort by Title</option>
                <option value="status">Sort by Status</option>
                <option value="severity">Sort by Severity</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Incidents Table */}
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Crisis Type</th>
                  <th>Status</th>
                  <th>Severity</th>
                  <th>CMT</th>
                  <th>Tasks</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredIncidents.map((incident) => (
                  <tr key={incident.id}>
                    <td>{incident.title}</td>
                    <td>{incident.crisisType === 'Other' && incident.customCrisisType ? incident.customCrisisType : incident.crisisType}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(incident.status)}`}>
                        {incident.status}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getSeverityBadgeClass(incident.severity)}`}>
                        {incident.severity}
                      </span>
                    </td>
                    <td>
                      {(() => {
                        // Get CMT info for this incident
                        const incidentCmt = availableCmts.find(cmt => cmt.id === incident.cmtId);
                        
                        if (!incident.cmtId) {
                          return <span className="text-muted">Legacy</span>;
                        }
                        
                        return (
                          <span 
                            className="badge bg-primary"
                            style={{ 
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}
                          >
                            {incident.cmtName || cmtIdToName[incident.cmtId] || incidentCmt?.name || incident.cmtId}
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      {(() => {
                        const badgeState = taskBadgeStates.get(incident.id);
                        
                        if (!badgeState || badgeState.totalCount === 0) {
                          return <span className="text-muted">-</span>;
                        }
                        
                        const { totalCount, overdueCount, state } = badgeState;
                        
                        // Determine badge styling based on state
                        let badgeClass = 'badge ';
                        let badgeStyle = { 
                          fontSize: '0.8rem', 
                          padding: '4px 8px',
                          fontWeight: 'bold'
                        };
                        
                        if (overdueCount > 0) {
                          badgeClass += 'bg-danger task-badge-critical';
                        } else {
                          badgeClass += 'bg-primary';
                        }
                        
                        // Generate badge text based on task counts
                        let badgeText = '';
                        
                        if (overdueCount > 0) {
                          if (totalCount === 1 && overdueCount === 1) {
                            // Special case: Single overdue task
                            badgeText = '1 overdue task';
                          } else {
                            // Multiple tasks with some overdue: "5 tasks, 2 overdue"
                            badgeText = `${totalCount} task${totalCount !== 1 ? 's' : ''}, ${overdueCount} overdue`;
                          }
                        } else {
                          // Show just total tasks: "4 tasks" or "1 task"
                          badgeText = `${totalCount} task${totalCount !== 1 ? 's' : ''}`;
                        }
                        
                        return (
                          <span 
                            className={badgeClass}
                            style={badgeStyle}
                          >
                            {badgeText}
                          </span>
                        );
                      })()}
                    </td>
                    <td>{incident.createdAt?.toDate ? incident.createdAt.toDate().toLocaleString() : new Date(incident.createdAt).toLocaleString()}</td>
                    <td>
                      <Tooltip title="Open incident details to view all crisis information, manage tasks, timeline, communications, and more.">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => {
                            console.log('Preloading timeline data for incident:', incident.id);
                            preloadTimelineData(incident.id);
                            navigate(`/incidents/${incident.id}`);
                          }}
                          style={{ borderColor: '#39325b', color: '#39325b' }}
                        >
                          View
                        </button>
                      </Tooltip>
                      <Tooltip title={(() => {
                        const isOwnCmt = incident.cmtId === activeCmt?.id;
                        const isAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'admin' || currentUser?.isSuperAdmin;
                        const isCmtLeader = currentUser?.role === 'cmt_leader' || currentUser?.role === 'cmt_leader_alt' || currentUser?.role === 'cmt_admin';
                        return isOwnCmt || isAdmin || isCmtLeader
                          ? 'Remove this incident from the incidents list. The incident will remain in audit logs but will no longer be visible here.'
                          : 'Read-only — child CMT incident';
                      })()}>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteIncident(incident)}
                          style={{ borderColor: '#dc3545', color: '#dc3545' }}
                          disabled={(() => {
                            const isOwnCmt = incident.cmtId === activeCmt?.id;
                            const isAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'admin' || currentUser?.isSuperAdmin;
                            const isCmtLeader = currentUser?.role === 'cmt_leader' || currentUser?.role === 'cmt_leader_alt' || currentUser?.role === 'cmt_admin';
                            
                            // Delete permissions check
                            
                            return !(isOwnCmt || isAdmin || isCmtLeader);
                          })()}
                        >
                          Delete
                        </button>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>Delete Incident</span>
          </div>
        }
        open={deleteModalVisible}
        onOk={confirmDeleteIncident}
        onCancel={cancelDeleteIncident}
        okText="Delete Incident"
        okButtonProps={{ 
          danger: true,
          loading: deleting,
          disabled: deleteConfirmText !== 'DELETE'
        }}
        cancelText="Cancel"
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Are you sure you want to delete <strong>"{incidentToDelete?.title}"</strong>?</p>
          <div style={{ 
            backgroundColor: '#fff2f0', 
            border: '1px solid #ffccc7', 
            borderRadius: '6px', 
            padding: '12px', 
            marginBottom: '16px' 
          }}>
            <p style={{ margin: 0, fontSize: '14px' }}>
              <strong>Important:</strong> This incident will be removed from the incidents list but will <strong>remain in auditable logs</strong> for compliance purposes. This action cannot be undone.
            </p>
          </div>
          <p><strong>Crisis Type:</strong> {incidentToDelete?.crisisType}</p>
          <p><strong>Status:</strong> {incidentToDelete?.status}</p>
          <p><strong>Created:</strong> {incidentToDelete?.createdAt?.toDate ? incidentToDelete.createdAt.toDate().toLocaleString() : new Date(incidentToDelete?.createdAt).toLocaleString()}</p>
        </div>
        <div>
          <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>Type "DELETE" to confirm:</p>
          <Input
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="DELETE"
            disabled={deleting}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Incidents;